import { ChangeDetectorRef, Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { EngineService } from '@core/services/engine/engine.service';
import { ModalService } from '@core/services/modal/modal.service';
import { ShareService } from '@core/services/share-data/share-data.service';
import { PassDataService } from '@core/services/pass-data/pass-data.service';
import { OpeningOverlayComponent } from '@features/opening-overlay/opening-overlay.component';
import { DetectionErrorModalComponent } from '@shared/modals/detection-error-modal/detection-error-modal.component';
import {
	catchError,
	combineLatest,
	concatMap,
	EMPTY,
	filter,
	finalize,
	first,
	from,
	map,
	Observable,
	Subscription,
	tap,
	throwError,
} from 'rxjs';
import { ModelFitterService } from '@core/services/engine/services/modelFitter.service';
import { CONFIG, IDLE_TIMEOUT_MS, SELECTORS, STORAGE_NAMES } from '@root/app.config';
import { Opening, Point } from '@root/app.interfaces';
import { ConfirmModalComponent } from '@shared/modals/confirm-modal/confirm-modal.component';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { FitModelError } from '@core/errors/fit-model-error';
import { SelectBackgroundModalComponent } from '@shared/modals/select-background-modal/select-background-modal.component';
import { QuizModalComponent } from '@shared/modals/quiz-modal/quiz-modal.component';
import { timer } from 'rxjs';
import { switchMap, delay } from 'rxjs/operators';
import { DetectionService } from '@core/services/detection/detection.service';
import { TranslateService } from '@ngx-translate/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';

@Component({
	selector: 'app-select-opening-screen',
	templateUrl: './select-opening-screen.component.html',
	styleUrls: ['./select-opening-screen.component.scss', './select-opening-screen.responsive.scss'],
})
export class SelectOpeningScreenComponent implements OnInit, OnDestroy {
	@ViewChild(OpeningOverlayComponent, { static: false }) openingOverlay: OpeningOverlayComponent | null = null;
	loading = true;
	enableFitBlinds = false;
	fitBlindsPressed = false;
	noDetection = false;
	isMobile = this.passDataService.isMobileOrTablet;
	isBannerHidden = false;
	touchStartY = 0;
	touchEndY = 0;
	blindDisplayStatusesInternal: Record<string, boolean> = {};
	blindPreviouslyAssigned: Record<string, boolean> = {};
	keepSelected = false;

	private readonly skipFadeDuration = 5; // seconds
	isSkipVisible = false;
	isSkipAvailable = false;
	isSkipNoFade = true;
	private readonly skipButtonDelay = 20; // seconds

	changeImageDialogRef: MatDialogRef<ConfirmModalComponent> = null;
	private popoverTimeout: ReturnType<typeof setTimeout>;

	getDetectionStart: Subscription;
	getDetectionDone: Subscription;
	getIsAnOpeningSelected: Subscription;
	getDetectionError: Subscription;

	constructor(
		private shareService: ShareService,
		private sessionStorageService: SessionStorageService,
		private engineService: EngineService,
		private passDataService: PassDataService,
		private changeDetection: ChangeDetectorRef,
		private modelFitterService: ModelFitterService,
		private dialog: MatDialog,
		private modalService: ModalService,
		private detectionService: DetectionService,
		private translateService: TranslateService,
	) {}

	ngOnInit(): void {
		this.detectionDoneHandler();
		this.openingsSelectedHandler();
		this.detectionErrorHandler();

		this.getDetectionStart = this.shareService.getDetectionStart
			.pipe(
				tap(() => {
					this.isSkipNoFade = true;
					this.isSkipVisible = false;
					this.isSkipAvailable = false;
					this.changeDetection.detectChanges();
					setTimeout(() => {
						this.isSkipNoFade = false;
						this.changeDetection.detectChanges();
					});
				}),
				switchMap(() => timer(this.skipButtonDelay * 1000)),
				tap(() => {
					this.isSkipVisible = true;
					this.changeDetection.detectChanges();
				}),
				delay(this.skipFadeDuration * 1000),
				tap(() => {
					this.isSkipAvailable = true;
					this.changeDetection.detectChanges();
				}),
			)
			.subscribe();
	}

	ngOnDestroy(): void {
		this.shareService.setSelectOpeningMode(false);
		this.getDetectionDone.unsubscribe();
		this.getIsAnOpeningSelected?.unsubscribe();
		this.getDetectionStart.unsubscribe();

		if (this.popoverTimeout) {
			clearTimeout(this.popoverTimeout);
		}
	}

	detectionDoneHandler(): void {
		this.getDetectionDone = this.shareService.getDetectionDone.subscribe((detectionDone: boolean) => {
			this.loading = !detectionDone;
			this.changeDetection.detectChanges();

			if (detectionDone && this.openingOverlay) {
				if (!this.noDetection) {
					this.openingOverlay.initOpenings(this.keepSelected);
				} else {
					this.openingOverlay.initWithoutOpenings();
				}

				this.keepSelected = false;
				this.isSkipNoFade = true;
				this.isSkipVisible = true;
				this.isSkipAvailable = true;
				this.changeDetection.detectChanges();
				setTimeout(() => {
					this.isSkipNoFade = false;
					this.changeDetection.detectChanges();
				});
				this.noDetection = false;
			}
		});
	}

	detectionErrorHandler(): void {
		this.getDetectionError = this.shareService.getDetectionError.subscribe(() => {
			this.noDetection = true;
			this.dialog.open(DetectionErrorModalComponent, {
				...this.modalService.getConfig(DetectionErrorModalComponent.ID),
				data: { type: 'detection_error' },
			});
		});
	}

	openingsSelectedHandler(): void {
		this.getIsAnOpeningSelected = this.shareService.getIsAnOpeningSelected.subscribe((isAnOpeningSelected: boolean) => {
			this.enableFitBlinds = isAnOpeningSelected;
			this.changeDetection.detectChanges();
		});
	}

	onChangeImage(): void {
		this.changeImageDialogRef = this.modalService.openResponsiveDialog({
			...{ component: ConfirmModalComponent, data: { page: 'change_image' } },
			...this.modalService.getConfig(ConfirmModalComponent.ID),
		});

		this.changeImageDialogRef.afterClosed().subscribe((closeStatus: boolean) => {
			this.changeImageDialogRef = null;

			if (closeStatus) {
				this.modalService.openResponsiveDialog({
					component: SelectBackgroundModalComponent,
					width: 'max-content',
					maxWidth: '940px',
					height: 'auto',
					minHeight: '150px',
					data: { blind_detection: true },
				});
			}
		});
	}

	onFitBlinds(): void {
		const openings = this.openingOverlay?.getOpenings().sort((a, b) => {
			const aNum = a.displayNumber ?? Infinity;
			const bNum = b.displayNumber ?? Infinity;
			return aNum - bNum;
		});

		this.fitBlindsPressed = true;
		this.shareService.setIsAnOpeningSelected(false);
		let openFitErrorModal = null;
		this.blindDisplayStatusesInternal = this.shareService.getblindDisplayStatusesResponseValue() ?? {};
		this.shareService.setblindDisplayStatusesResponse({}, true);

		// Need to completely reset the position of the Blinds when restarting detection via RotateDeviceModalComponent.
		if (this.shareService.getOpeningFitResetValue()) {
			openings.forEach((op) => (op.isFitted = false));
		}

		from(openings ?? [])
			.pipe(
				filter((op) => op.isSelected && !op.isFitted),
				concatMap((op) =>
					this.processOpening(op).pipe(
						catchError((err) => {
							if (err instanceof FitModelError) {
								if (!openFitErrorModal) {
									openFitErrorModal = this.dialog.open(DetectionErrorModalComponent, {
										...this.modalService.getConfig(DetectionErrorModalComponent.ID),
										data: { type: 'fit_blind_error' },
									});
								}
								return EMPTY;
							}
							return throwError(() => err);
						}),
					),
				),
				finalize(() => {
					this.openingOverlay?.setOpenings(openings);
					this.onExit(openings);
				}),
			)
			.subscribe();
	}

	processOpening(opening: Opening): Observable<string> {
		if (!this.isOpeningRealistic(opening.coordinates)) {
			// Now the error info is not going to show up in the modal
			return throwError(
				() => new FitModelError(this.translateService.instant('fit_blind_error.exception', { openingId: opening.id }), opening.coordinates),
			);
		}

		this.shareService.setSafeToFit(null);
		const id = opening.blindId ?? Math.floor(1000 + Math.random() * 9000);
		const idStr = id.toString();

		const fitBlind = tap(() => {
			this.engineService.resetBoundingBoxDisplay(idStr, true); // resets blind to be straight
			this.modelFitterService.fitModelToQuad(opening.coordinates);
			opening.isFitted = true;
			this.blindDisplayStatusesInternal[id] = true;
			this.blindPreviouslyAssigned[id] = true;
		});

		let ready: Observable<string>;

		if (opening.blindId) {
			ready = combineLatest([
				// need to check for both so that the boundingBox is applied to the correct blind and all other model manipulations are done before fitting the model
				this.shareService.getBboxApplied,
				this.shareService.getSafeToFit,
			]).pipe(
				filter(([bboxId, blindId]) => bboxId === idStr && blindId === idStr),
				first(),
				fitBlind,
				map(() => idStr),
			);

			this.shareService.setSelectedModel(opening.blindId.toString()); // Going with this for now as if the last opening has a blindId it needs this called // after many issues managed to remove race conditions so this should be ok now
		} else {
			ready = this.shareService.getBboxApplied.pipe(
				filter((bboxId) => bboxId === idStr),
				first(),
				fitBlind,
				map(() => idStr),
			);

			// this.engineService.createScene(opening.blindType, idStr);
			this.shareService.getModelLoaded
				.pipe(first())
				.subscribe(this.engineService.createScene.bind(this.engineService, opening.blindType, idStr));
			this.shareService.setBlindType({ type: opening.blindType, id });
			opening.blindId = id;
		}

		return ready;
	}

	onExit(openings: Opening[] = null): void {
		const blindListData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
		this.shareService.setSelectOpeningMode(false);
		this.shareService.setOpeningFitReset(false);

		if (openings) {
			blindListData.forEach(({ blind_id }) => {
				// If was assigned and is now not -> hide
				if (this.blindPreviouslyAssigned[blind_id] && !openings.some((opening) => opening.blindId === blind_id)) {
					this.blindDisplayStatusesInternal[blind_id] = false;
					this.blindPreviouslyAssigned[blind_id] = false;
					// this.engineService.resetBoundingBoxDisplay(blind_id, true); // resets wrong blind
				}
				// If it already has a display status we keep it and if not it must be new and unassigned so we hide it
				const shouldShow = Object.hasOwn(this.blindDisplayStatusesInternal, blind_id) ? this.blindDisplayStatusesInternal[blind_id] : false;

				this.engineService.toggleRootMeshById(blind_id, shouldShow);
			});
		} else {
			const blindDisplayStatuses = this.shareService.getblindDisplayStatusesResponseValue();
			if (blindDisplayStatuses) {
				blindListData.forEach(({ blind_id }) => {
					// If it already has a display status we keep it and if not it must be new so we show it
					const shouldShow = Object.hasOwn(blindDisplayStatuses, blind_id) ? blindDisplayStatuses[blind_id] : true;

					this.engineService.toggleRootMeshById(blind_id, shouldShow);
				});
			} else {
				this.engineService.toggleAllRootMeshes(true);
			}
		}

		this.fitBlindsPressed = false;
	}

	onSkip(): void {
		// TODO: Change skip functionality depending on stage
		this.openingOverlay.resetOnSkip();
		this.onExit();
	}

	restartAutoDetection(): void {
		this.modalService
			.openResponsiveDialog({
				...{ component: ConfirmModalComponent, data: { page: 'restart_auto_detection' } },
				...this.modalService.getConfig(ConfirmModalComponent.ID),
			})
			.afterClosed()
			.subscribe((closeStatus: boolean) => {
				if (closeStatus) {
					this.detectionService.retryLast();
					this.keepSelected = true;
				}
			});
	}

	onTouchEvent(event: TouchEvent): void {
		if (window.innerWidth > CONFIG.breakpoints['tablet-landscape']) {
			return;
		}

		const target = event.target as Element;
		if (target.closest(SELECTORS.opening_banner_buttons)) {
			return;
		}

		switch (event.type) {
			case 'touchstart':
				this.touchStartY = event.touches[0].clientY;
				break;
			case 'touchmove':
				this.touchEndY = event.touches[0].clientY;
				this.checkSwipe(this.touchEndY - this.touchStartY);
				break;
			case 'touchend':
				this.checkSwipe(this.touchEndY - this.touchStartY);
				break;
		}
	}

	checkSwipe(swipeDistance: number) {
		if (swipeDistance < -20 && !this.isBannerHidden) {
			this.isBannerHidden = true;
		} else if (swipeDistance > 20 && this.isBannerHidden) {
			this.isBannerHidden = false;
		}
	}

	onOpenQuizModal(): void {
		if (!this.dialog.getDialogById(QuizModalComponent.ID)) {
			this.dialog.open(QuizModalComponent, this.modalService.getConfig(QuizModalComponent.ID));
		}
	}

	/**
	 * Detemine an opening is realistic or not
	 * If it is too small, too big or the width and height are disproportionate, it is unrealistic
	 */
	private isOpeningRealistic(coordinates: Point[]): boolean {
		// Adjust them if needed, or add more conditions such as aspect ratio etc...
		const minWidth = 10;
		const minHeight = 10;

		if (coordinates.length !== 4) {
			return false;
		}

		// [left_top, right_top, right_bottom, left_bottom]
		const width = Math.abs(coordinates[0].x - coordinates[1].x);
		const height = Math.abs(coordinates[0].y - coordinates[3].y);

		if (width < minWidth || height < minHeight) {
			return false;
		}

		const crossProduct = (p1: Point, p2: Point, p3: Point): number => {
			return (p2.x - p1.x) * (p3.y - p1.y) - (p2.y - p1.y) * (p3.x - p1.x);
		};

		const isIntersecting = (p1: Point, p2: Point, p3: Point, p4: Point): boolean => {
			const cross1 = crossProduct(p1, p2, p3);
			const cross2 = crossProduct(p1, p2, p4);
			const cross3 = crossProduct(p3, p4, p1);
			const cross4 = crossProduct(p3, p4, p2);

			return cross1 * cross2 < 0 && cross3 * cross4 < 0;
		};

		const isRectangleIntersectant = (p1: Point, p2: Point, p3: Point, p4: Point): boolean => {
			const notIntersecting1 = !isIntersecting(p1, p2, p3, p4);
			const notIntersecting2 = !isIntersecting(p1, p2, p4, p3);
			const notIntersecting3 = !isIntersecting(p2, p3, p1, p4);
			const notIntersecting4 = !isIntersecting(p3, p4, p1, p2);

			return !(notIntersecting1 && notIntersecting2 && notIntersecting3 && notIntersecting4);
		};

		return !isRectangleIntersectant(coordinates[0], coordinates[1], coordinates[2], coordinates[3]);
	}

	onShowPopover(popover: NgbPopover): void {
		if (this.popoverTimeout) {
			clearTimeout(this.popoverTimeout);
		}

		if (!popover.isOpen()) {
			popover.open();
		}

		this.popoverTimeout = setTimeout(() => {
			popover.close();
		}, IDLE_TIMEOUT_MS);
	}
}
